import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Table,
  Tabs,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import getCompanyIdFromToken from "../../utils/getCompanyIdFromToken";
import { loadAllDesignation } from "../../redux/rtk/features/designation/designationSlice";
import { CompanyDetailsGetbyId } from "../../redux/rtk/features/settingSlice/settingSlice";
import { loadEmailConfig } from "../../redux/rtk/features/masters/masterSlice";
import { getDepartments } from "../../components/department/departmentApis";
import axios from "axios";
import {
  deleteTemplate,
  extractPlaceholders,
  getTemplatesByCompanyId,
  saveTemplate,
} from "../../redux/rtk/features/letterTemplate/letterTemplateSlice";
import {
  getFieldsByCompanyId,
  saveField,
} from "../../redux/rtk/features/letterTemplate/letterTemplateFieldSlice";
import {
  DEFAULT_APPOINTMENT_LETTER_TEMPLATE,
  DEFAULT_OFFER_LETTER_TEMPLATE,
  getPlaceholderFieldType,
  getPlaceholderLabel,
  PLACEHOLDER_LABELS,
} from "./defaultLetterTemplates";
import PlaceholderSuggestTextArea from "./PlaceholderSuggestTextArea";
import "./LetterGeneration.css";

const { TextArea } = Input;

const LETTER_TEMPLATE_FORM_PATH = "/admin/hr/letter-generation/template";

const BASE_URL = process.env.REACT_APP_API
  ? process.env.REACT_APP_API.replace(/^["']|["']$/g, "")
  : "";

const defaultCompanyName = "Company Name";
const defaultCompanyAddress = "Company Address";

const TITLE_OPTIONS = [
  { value: "Mr.", label: "Mr." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Miss", label: "Miss" },
  { value: "Ms.", label: "Ms." },
  { value: "Dr.", label: "Dr." },
  { value: "Prof.", label: "Prof." },
];

/** Replace [Key] in content with values from map; escape HTML in values for safety */
function replacePlaceholders(content, values) {
  if (!content) return "";
  return content.replace(/\[([^\]]+)\]/g, (_, key) => {
    const k = key.trim();
    const v = values[k];
    if (v == null || v === "") return "________________";
    const str = String(v);
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  });
}

/**
 * Convert simple text (as written in Word) to letter HTML.
 * - Double newline = new paragraph (with spacing).
 * - Single newline = line break within paragraph.
 * System applies this styling so user only pastes plain text.
 */
function plainTextToLetterHtml(text) {
  if (!text || typeof text !== "string") return "";
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const paragraphs = normalized.split(/\n\n+/).map((block) => block.trim()).filter(Boolean);
  if (paragraphs.length === 0) return "";
  return paragraphs
    .map(
      (para) =>
        "<p>" + para.replace(/\n/g, "<br/>") + "</p>"
    )
    .join("\n");
}

/** Build placeholder values from hiring candidate and job (e.g. when opened from Offer tab). */
function buildPlaceholderValuesFromCandidate(candidate, job, designationList, departmentList) {
  const first = candidate?.FirstName ?? candidate?.firstName ?? "";
  const middle = candidate?.MiddleName ?? candidate?.middleName ?? "";
  const last = candidate?.LastName ?? candidate?.lastName ?? "";
  const name = [first, middle, last].filter(Boolean).join(" ") || (candidate?.name ?? candidate?.Name ?? "");
  const jobTitle =
    job?.Title ?? job?.title ?? job?.JobTitle ?? candidate?.JobTitle ?? candidate?.jobTitle ?? "";
  const departmentId = job?.DepartmentId ?? job?.departmentId ?? candidate?.DepartmentId ?? candidate?.departmentId;
  const designationId = job?.DesignationId ?? job?.designationId ?? candidate?.DesignationId ?? candidate?.designationId;
  const department =
    job?.DepartmentName ?? job?.departmentName ?? candidate?.DepartmentName ?? candidate?.departmentName
    ?? (departmentList || []).find((d) => (d.DepartmentId ?? d.id) === departmentId)?.DepartmentName
    ?? (departmentList || []).find((d) => (d.DepartmentId ?? d.id) === departmentId)?.name
    ?? "";
  const designation =
    job?.DesignationName ?? job?.designationName ?? candidate?.DesignationName ?? candidate?.designationName
    ?? (designationList || []).find((d) => (d.DesignationId ?? d.id) === designationId)?.DesignationName
    ?? (designationList || []).find((d) => (d.DesignationId ?? d.id) === designationId)?.name
    ?? "";
  const dateStr = dayjs().format("DD MMMM YYYY");
  return {
    Candidate_Name: name,
    Recipient_Name: name,
    Email: candidate?.Email ?? candidate?.email ?? "",
    Mobile: candidate?.Phone ?? candidate?.phone ?? candidate?.Mobile ?? "",
    Phone: candidate?.Phone ?? candidate?.phone ?? "",
    Job_Title: jobTitle,
    Designation: designation,
    Department: department,
    Joining_Date: dateStr,
    Letter_Date: dateStr,
    Date: dateStr,
    Address_Line1: candidate?.Address_Line1 ?? candidate?.addressLine1 ?? candidate?.Address ?? "",
    Address_Line2: candidate?.Address_Line2 ?? candidate?.addressLine2 ?? "",
    Address: candidate?.Address ?? candidate?.address ?? "",
    Parent_Guardian: candidate?.Parent_Guardian ?? candidate?.parentGuardian ?? "",
    Reporting_Manager_Name: candidate?.Reporting_Manager_Name ?? candidate?.reportingManagerName ?? "",
    Reporting_Manager_Designation: candidate?.Reporting_Manager_Designation ?? candidate?.reportingManagerDesignation ?? "",
    Alternative_Contact_Name: candidate?.Alternative_Contact_Name ?? candidate?.alternativeContactName ?? "",
    Alternative_Contact_Phone: candidate?.Alternative_Contact_Phone ?? candidate?.alternativeContactPhone ?? "",
  };
}

export default function LetterGeneration() {
  const dispatch = useDispatch();
  const companyId = getCompanyIdFromToken();
  const companyDetailsRaw = useSelector((state) => state.setting?.list);
  const companyDetails = Array.isArray(companyDetailsRaw)
    ? companyDetailsRaw[0]
    : companyDetailsRaw || null;
  const emailConfig = useSelector((state) => state.masters?.emailConfig);
  const emailConfigResolved = Array.isArray(emailConfig) ? emailConfig[0] : emailConfig;
  const designationList = useSelector((state) => state.designations?.list) || [];
  const templateList = useSelector((state) => state.letterTemplate?.list) || [];
  const templateLoading = useSelector((state) => state.letterTemplate?.loading);
  const fieldList = useSelector((state) => state.letterTemplateField?.list) || [];
  const letterRef = useRef(null);

  const [activeTab, setActiveTab] = useState("generate");
  const [letterType, setLetterType] = useState("offer");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [includeRecipientInCc, setIncludeRecipientInCc] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const prefillFromStateApplied = useRef(false);
  // Field manager state
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    fieldKey: "",
    fieldLabel: "",
    fieldType: "text",
    displayOrder: 0,
  });

  const companyName =
    (companyDetails && (companyDetails.CompanyName || companyDetails.companyName)) ||
    defaultCompanyName;
  const companyAddress =
    (companyDetails &&
      (companyDetails.RegisteredStreetAddress ||
        companyDetails.StreetAddress ||
        companyDetails.address)) ||
    defaultCompanyAddress;
  const companyLogoPath = companyDetails && (companyDetails.LogoPath || companyDetails.logoPath);
  const companyLogoUrl = companyLogoPath
    ? companyLogoPath.startsWith("http")
      ? companyLogoPath
      : `${BASE_URL}/${String(companyLogoPath).replace(/^\//, "")}`
    : null;

  useEffect(() => {
    dispatch(getTemplatesByCompanyId());
  }, [dispatch]);
  useEffect(() => {
    dispatch(getFieldsByCompanyId());
  }, [dispatch]);

  useEffect(() => {
    setCompanyLoading(true);
    dispatch(CompanyDetailsGetbyId(companyId || null)).finally(() =>
      setCompanyLoading(false)
    );
  }, [dispatch, companyId]);

  useEffect(() => {
    if (companyId) dispatch(loadEmailConfig(companyId));
  }, [dispatch, companyId]);

  useEffect(() => {
    dispatch(loadAllDesignation());
  }, [dispatch]);

  useEffect(() => {
    getDepartments()
      .then((data) => setDepartmentList(Array.isArray(data) ? data : []))
      .catch(() => setDepartmentList([]));
  }, []);

  const templateIdKey = (t) => t.id ?? t.Id ?? t.LetterTemplateId;
  const selectedTemplate = templateList.find(
    (t) => templateIdKey(t) === selectedTemplateId || templateIdKey(t) === Number(selectedTemplateId)
  );
  const selectedContent = selectedTemplate?.content ?? selectedTemplate?.TemplateContent ?? "";
  const selectedAnnexure = selectedTemplate?.annexureContent ?? selectedTemplate?.AnnexureContent ?? "";
  const placeholderKeysRaw = selectedTemplate
    ? [
        ...new Set([
          ...extractPlaceholders(selectedContent),
          ...extractPlaceholders(selectedAnnexure),
        ]),
      ]
    : [];
  const fieldByKey = (fieldList || []).reduce(
    (acc, f) => {
      const key = f.fieldKey ?? f.FieldKey ?? "";
      if (key) acc[key] = { fieldLabel: f.fieldLabel ?? f.FieldLabel, fieldType: f.fieldType ?? f.FieldType, displayOrder: f.displayOrder ?? f.DisplayOrder ?? 999 };
      return acc;
    },
    {}
  );
  const placeholdersInTemplate = placeholderKeysRaw.slice().sort((a, b) => {
    const orderA = fieldByKey[a]?.displayOrder ?? 999;
    const orderB = fieldByKey[b]?.displayOrder ?? 999;
    return orderA - orderB;
  });
  const designationOptions = (designationList || []).map((item) => ({
    value: item.DesignationName || item.name,
    label: item.DesignationName || item.name || "—",
  }));
  const departmentOptions = (departmentList || []).map((item) => ({
    value: item.DepartmentName || item.name,
    label: item.DepartmentName || item.name || "—",
  }));

  const placeholderSuggestionKeys = [
    ...new Set([
      ...Object.keys(PLACEHOLDER_LABELS || {}),
      ...(fieldList || []).map((f) => f.fieldKey ?? f.FieldKey).filter(Boolean),
    ]),
  ].sort((a, b) => a.localeCompare(b));

  const openAddTemplate = () => {
    navigate(`${LETTER_TEMPLATE_FORM_PATH}/new`);
  };

  const openEditTemplate = (template) => {
    navigate(`${LETTER_TEMPLATE_FORM_PATH}/${templateIdKey(template)}`);
  };

  const loadDefaultTemplates = () => {
    dispatch(
      saveTemplate({
        id: 0,
        name: DEFAULT_OFFER_LETTER_TEMPLATE.name,
        type: DEFAULT_OFFER_LETTER_TEMPLATE.type,
        content: DEFAULT_OFFER_LETTER_TEMPLATE.content,
        annexureContent: DEFAULT_OFFER_LETTER_TEMPLATE.annexureContent ?? "",
      })
    );
    dispatch(
      saveTemplate({
        id: 0,
        name: DEFAULT_APPOINTMENT_LETTER_TEMPLATE.name,
        type: DEFAULT_APPOINTMENT_LETTER_TEMPLATE.type,
        content: DEFAULT_APPOINTMENT_LETTER_TEMPLATE.content,
        annexureContent: DEFAULT_APPOINTMENT_LETTER_TEMPLATE.annexureContent ?? "",
      })
    );
  };

  const openAddField = () => {
    setEditingFieldId(null);
    setFieldForm({ fieldKey: "", fieldLabel: "", fieldType: "text", displayOrder: fieldList.length });
    setFieldModalVisible(true);
  };
  const openEditField = (record) => {
    setEditingFieldId(record.id ?? record.Id);
    setFieldForm({
      fieldKey: record.fieldKey ?? record.FieldKey ?? "",
      fieldLabel: record.fieldLabel ?? record.FieldLabel ?? "",
      fieldType: (record.fieldType ?? record.FieldType ?? "text").toLowerCase(),
      displayOrder: record.displayOrder ?? record.DisplayOrder ?? 0,
    });
    setFieldModalVisible(true);
  };
  const handleSaveField = () => {
    if (!fieldForm.fieldKey?.trim()) {
      toast.error("Enter field key (e.g. Custom_Field)");
      return;
    }
    if (!fieldForm.fieldLabel?.trim()) {
      toast.error("Enter field label");
      return;
    }
    dispatch(
      saveField({
        id: editingFieldId ?? 0,
        companyId: companyId ?? 0,
        fieldKey: fieldForm.fieldKey.trim(),
        fieldLabel: fieldForm.fieldLabel.trim(),
        fieldType: fieldForm.fieldType,
        displayOrder: Number(fieldForm.displayOrder) || 0,
      })
    );
    setFieldModalVisible(false);
  };

  const handleDeleteTemplate = (template) => {
    const id = templateIdKey(template);
    Modal.confirm({
      title: "Delete template?",
      content: "This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        dispatch(deleteTemplate({ id, companyId }));
      },
    });
  };

  const setPlaceholder = (key, value) => {
    setPlaceholderValues((prev) => ({ ...prev, [key]: value }));
  };

  const getDefaultPlaceholderValues = (letterTypeOverride) => {
    const t = letterTypeOverride ?? letterType;
    const dateStr = dayjs().format("DD MMMM YYYY");
    return {
      Letter_Type: t === "offer" ? "Offer Letter" : "Appointment Letter",
      Date: dateStr,
      Letter_Date: dateStr,
      Joining_Date: dateStr,
      Company_Legal_Name: companyName,
      Company_Team_Name: companyName,
      Company_Name: companyName,
      Company_Website: companyDetails?.Website || companyDetails?.website || "",
      Company_Management: companyName,
      Office_Location: "",
      Initial_Reporting_Location: "",
    };
  };

  useEffect(() => {
    if (selectedTemplateId && prefillFromStateApplied.current) {
      prefillFromStateApplied.current = false;
      return;
    }
    if (selectedTemplateId) {
      setPlaceholderValues((prev) => ({ ...getDefaultPlaceholderValues(), ...prev }));
    }
  }, [selectedTemplateId]);

  // Prefill from Hiring Offer tab: state = { letterType, candidate, job }
  useEffect(() => {
    const state = location.state;
    if (!state || (!state.candidate && !state.letterType)) return;
    const { letterType: type, candidate, job } = state;
    if (type) setLetterType(type);
    setActiveTab("generate");
    if (candidate) {
      const fromCandidate = buildPlaceholderValuesFromCandidate(
        candidate,
        job ?? null,
        designationList,
        departmentList
      );
      const defaults = getDefaultPlaceholderValues(type || letterType);
      setPlaceholderValues({ ...defaults, ...fromCandidate });
    }
    const firstTemplateOfType = (templateList || []).find(
      (t) => (t.type ?? t.LetterType) === (type || letterType)
    );
    if (firstTemplateOfType) {
      prefillFromStateApplied.current = true;
      setSelectedTemplateId(firstTemplateOfType.id ?? firstTemplateOfType.Id ?? firstTemplateOfType.LetterTemplateId);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, templateList]);

  const mergedPlaceholderValues = {
    ...getDefaultPlaceholderValues(),
    ...placeholderValues,
  };

  const rawFilled = selectedTemplate
    ? replacePlaceholders(selectedContent, mergedPlaceholderValues)
    : "";
  const isPlainText = rawFilled && !selectedContent.includes("<");
  const filledContent = isPlainText
    ? plainTextToLetterHtml(rawFilled)
    : rawFilled;

  const rawAnnexureFilled =
    selectedAnnexure.trim() &&
    replacePlaceholders(selectedAnnexure, mergedPlaceholderValues);
  const isAnnexurePlainText =
    rawAnnexureFilled && !selectedAnnexure.includes("<");
  const filledAnnexure =
    rawAnnexureFilled &&
    (isAnnexurePlainText
      ? plainTextToLetterHtml(rawAnnexureFilled)
      : rawAnnexureFilled);

  const previewHtml = (
    <div className="letter-preview-content" ref={letterRef}>
      <div className="letter-letterhead letter-letterhead-row">
        {companyLogoUrl && (
          <div className="letter-logo-wrap">
            <img src={companyLogoUrl} alt="Company logo" className="letter-logo" />
          </div>
        )}
        <div className="letter-company-info">
          <h2>{companyName}</h2>
          <p>{companyAddress}</p>
        </div>
      </div>
      <div className="letter-date">
        Date: {mergedPlaceholderValues.Letter_Date || mergedPlaceholderValues.Date || dayjs().format("DD MMMM YYYY")}
      </div>
      {filledContent ? (
        <>
          <div
            className={`letter-template-body ${isPlainText ? "letter-plain-text" : ""}`}
            dangerouslySetInnerHTML={{ __html: filledContent }}
          />
          {filledAnnexure && (
            <div className="letter-annexure">
              <h4 className="letter-annexure-title">Annexure</h4>
              <div
                className={`letter-template-body ${isAnnexurePlainText ? "letter-plain-text" : ""}`}
                dangerouslySetInnerHTML={{ __html: filledAnnexure }}
              />
            </div>
          )}
        </>
      ) : (
        <div className="letter-preview-placeholder">
          <p>Select a template and fill in the details to preview the letter.</p>
        </div>
      )}
    </div>
  );

  const getLetterPdfBlob = async () => {
    if (!letterRef.current) return null;
    const el = letterRef.current;
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: el.scrollWidth,
      height: el.scrollHeight,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfW = 210;
    const pdfH = 297;
    const margin = 20;
    const bottomSafe = 12;
    const contentW = pdfW - 2 * margin;
    const contentH = pdfH - 2 * margin - bottomSafe;
    const aspect = canvas.width / canvas.height;
    let drawW, drawH;
    if (contentW / contentH > aspect) {
      drawH = contentH;
      drawW = contentH * aspect;
    } else {
      drawW = contentW;
      drawH = contentW / aspect;
    }
    drawH = Math.min(drawH, contentH);
    drawW = Math.min(drawW, contentW);
    const x = margin + (contentW - drawW) / 2;
    const y = margin;
    pdf.addImage(imgData, "PNG", x, y, drawW, drawH);
    return pdf.output("blob");
  };

  const employeeName =
    mergedPlaceholderValues.Candidate_Name?.trim() ||
    mergedPlaceholderValues.Recipient_Name?.trim() ||
    "Employee";

  const handleDownloadPdf = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template first");
      return;
    }
    if (!letterRef.current) return;
    setPdfLoading(true);
    try {
      const blob = await getLetterPdfBlob();
      if (!blob) {
        toast.error("Failed to generate PDF");
        return;
      }
      const fileName = `${letterType}-letter-${String(employeeName).replace(/\s+/g, "-")}-${dayjs().format("YYYY-MM-DD")}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSendEmail = () => {
    setEmailSubject(
      `${letterType === "offer" ? "Offer" : "Appointment"} Letter - ${companyName}`
    );
    setEmailBody(
      `Please find attached your ${letterType === "offer" ? "Offer" : "Appointment"} Letter.`
    );
    setEmailTo(mergedPlaceholderValues.Email || "");
    setEmailModalVisible(true);
  };

  const handleEmailSubmit = async () => {
    if (!emailTo.trim()) {
      toast.error("Please enter recipient email");
      return;
    }
    setEmailSending(true);
    try {
      const blob = await getLetterPdfBlob();
      if (!blob) {
        toast.error("Failed to generate PDF");
        setEmailSending(false);
        return;
      }
      const letterTypeValue =
        letterType === "offer" ? "Offer Letter" : "Appointment Letter";
      const fileName = `${letterType}-letter-${String(employeeName).replace(/\s+/g, "-")}-${dayjs().format("YYYY-MM-DD")}.pdf`;
      const formData = new FormData();
      formData.append("file", blob, fileName);
      formData.append("toEmail", emailTo.trim());
      formData.append("letterType", letterTypeValue);
      formData.append("recipientName", employeeName);
      formData.append("name", employeeName);
      formData.append("letterDate", dayjs().format("YYYY-MM-DD"));
      formData.append("date", dayjs().format("YYYY-MM-DD"));

      // CC: comma-separated string (backend may also accept ccEmails array)
      const ccList = (emailCc || "")
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      if (includeRecipientInCc && emailTo.trim()) {
        ccList.unshift(emailTo.trim());
      }
      if (ccList.length > 0) {
        formData.append("ccEmail", ccList.join(", "));
      }

      await axios.post("employee/SendEmploymentLetterByEmail", formData, {
        headers: { Accept: "application/json" },
      });

      toast.success("Email sent successfully");
      setEmailModalVisible(false);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.response?.data?.error || err.message;
      toast.error(msg || "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  const templateColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span>
          <strong>{record.name ?? record.TemplateName ?? "—"}</strong>
          <span className="letter-template-type-badge">{record.type ?? record.LetterType ?? ""}</span>
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditTemplate(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteTemplate(record)}
          />
        </>
      ),
    },
  ];

  return (
    <div className="letter-generation-page">
      <Card className="letter-generation-card" loading={companyLoading || templateLoading}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "generate",
              label: "Generate Letter",
              children: null,
            },
            {
              key: "templates",
              label: "Manage Templates",
              children: null,
            },
          ]}
        />

        {activeTab === "templates" && (
          <div className="letter-templates-section">
            {templateList.length === 0 && (
              <div className="letter-templates-empty">
                <p>No templates yet. Add a template by pasting your letter content (use dynamics in square brackets, e.g. [Candidate_Name], [Job_Title]) or load the default formats.</p>
                <Button type="primary" onClick={loadDefaultTemplates} size="large">
                  Load default templates
                </Button>
              </div>
            )}
            {templateList.length > 0 && (
              <>
                <div className="letter-templates-actions">
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAddTemplate}>
                    Add template
                  </Button>
                </div>
                <Table
                  rowKey={(record) => templateIdKey(record) ?? record.id ?? "row"}
                  dataSource={templateList}
                  columns={templateColumns}
                  pagination={false}
                  size="small"
                />
              </>
            )}
            <div style={{ marginTop: 24 }}>
              <h4>Template fields (custom placeholders)</h4>
              <p className="letter-hint" style={{ marginBottom: 8 }}>
                Define custom [Field_Key] placeholders and their labels/types for the letter form.
              </p>
              <div className="letter-templates-actions" style={{ marginBottom: 12 }}>
                <Button type="default" icon={<PlusOutlined />} onClick={openAddField}>
                  Add field
                </Button>
              </div>
              <Table
                rowKey={(r) => r.id ?? r.Id ?? r.fieldKey ?? "f"}
                dataSource={fieldList}
                columns={[
                  { title: "Key", key: "fieldKey", render: (_, r) => r.fieldKey ?? r.FieldKey },
                  { title: "Label", key: "fieldLabel", render: (_, r) => r.fieldLabel ?? r.FieldLabel },
                  { title: "Type", key: "fieldType", render: (_, r) => r.fieldType ?? r.FieldType },
                  { title: "Order", key: "displayOrder", width: 80, render: (_, r) => r.displayOrder ?? r.DisplayOrder },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 80,
                    render: (_, r) => (
                      <Button type="text" icon={<EditOutlined />} onClick={() => openEditField(r)} />
                    ),
                  },
                ]}
                pagination={false}
                size="small"
              />
            </div>
          </div>
        )}

        {activeTab === "generate" && (
          <>
            <Segmented
              options={[
                { label: "Offer Letter", value: "offer" },
                { label: "Appointment Letter", value: "appointment" },
              ]}
              value={letterType}
              onChange={(v) => {
                setLetterType(v);
                setPlaceholderValues((prev) => ({
                  ...prev,
                  Letter_Type: v === "offer" ? "Offer Letter" : "Appointment Letter",
                }));
              }}
              block
              size="large"
              className="letter-type-segmented"
            />

            <Row gutter={[24, 16]} style={{ marginTop: 24 }}>
              <Col xs={24} lg={10}>
                <div className="letter-form-section">
                  <h4>Select template</h4>
                  <Select
                    placeholder="Choose a letter template"
                    value={selectedTemplateId || undefined}
                    onChange={(id) => {
                      setSelectedTemplateId(id);
                      setPlaceholderValues((prev) => ({ ...getDefaultPlaceholderValues(), ...prev }));
                    }}
                    options={templateList
                      .filter((t) => (t.type ?? t.LetterType) === letterType)
                      .map((t) => ({
                        value: templateIdKey(t),
                        label: t.name ?? t.TemplateName ?? t.id ?? templateIdKey(t),
                      }))}
                    style={{ width: "100%", marginBottom: 16 }}
                    allowClear
                  />
                  {templateList.length === 0 && (
                    <p className="letter-hint">
                      Go to &quot;Manage Templates&quot; to add or load default templates.
                    </p>
                  )}

                  {selectedTemplate && placeholdersInTemplate.length > 0 && (
                    <>
                      <h4 style={{ marginTop: 16 }}>Fill dynamics (from template)</h4>
                      <p className="letter-hint" style={{ marginBottom: 12 }}>
                        Values are filled from company settings where possible; edit as needed for this candidate.
                      </p>
                      {placeholdersInTemplate.map((key) => {
                        const label = fieldByKey[key]?.fieldLabel ?? getPlaceholderLabel(key);
                        const fieldType = (fieldByKey[key]?.fieldType ?? getPlaceholderFieldType(key))?.toLowerCase?.() ?? "text";
                        return (
                          <div key={key} style={{ marginBottom: 12 }}>
                            {fieldType === "date" ? (
                              <DatePicker
                                placeholder={label}
                                value={
                                  mergedPlaceholderValues[key]
                                    ? dayjs(mergedPlaceholderValues[key], [
                                        "DD MMMM YYYY",
                                        "YYYY-MM-DD",
                                      ])
                                    : null
                                }
                                onChange={(d) =>
                                  setPlaceholder(
                                    key,
                                    d ? d.format("DD MMMM YYYY") : ""
                                  )
                                }
                                style={{ width: "100%" }}
                              />
                            ) : fieldType === "designation" ? (
                              <Select
                                placeholder={label}
                                value={mergedPlaceholderValues[key] || undefined}
                                onChange={(v) => setPlaceholder(key, v)}
                                options={designationOptions}
                                allowClear
                                showSearch
                                filterOption={(input, opt) =>
                                  (opt?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                style={{ width: "100%" }}
                              />
                            ) : fieldType === "department" ? (
                              <Select
                                placeholder={label}
                                value={mergedPlaceholderValues[key] || undefined}
                                onChange={(v) => setPlaceholder(key, v)}
                                options={departmentOptions}
                                allowClear
                                style={{ width: "100%" }}
                              />
                            ) : fieldType === "textarea" ? (
                              <TextArea
                                placeholder={label}
                                value={mergedPlaceholderValues[key] || ""}
                                onChange={(e) =>
                                  setPlaceholder(key, e.target.value)
                                }
                                rows={2}
                                style={{ width: "100%" }}
                              />
                            ) : (
                              <Input
                                placeholder={label}
                                value={mergedPlaceholderValues[key] || ""}
                                onChange={(e) =>
                                  setPlaceholder(key, e.target.value)
                                }
                                style={{ width: "100%" }}
                              />
                            )}
                          </div>
                        );
                      })}
                      <div className="letter-actions">
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadPdf}
                          loading={pdfLoading}
                        >
                          Download PDF
                        </Button>
                        <Button icon={<MailOutlined />} onClick={handleSendEmail}>
                          Send by Email
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Col>
              <Col xs={24} lg={14}>
                <div className="letter-preview-section">
                  <h4>Preview</h4>
                  <div className="letter-preview-paper">{previewHtml}</div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Card>

      <Modal
        title={editingFieldId ? "Edit field" : "Add field"}
        open={fieldModalVisible}
        onOk={handleSaveField}
        onCancel={() => setFieldModalVisible(false)}
        okText="Save"
      >
        <div style={{ marginBottom: 12 }}>
          <label>Field key (placeholder name in template)</label>
          <Input
            placeholder="e.g. Custom_Field (use as [Custom_Field] in content)"
            value={fieldForm.fieldKey}
            onChange={(e) =>
              setFieldForm((p) => ({ ...p, fieldKey: e.target.value }))
            }
            style={{ marginTop: 4 }}
            disabled={!!editingFieldId}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Field label (shown in form)</label>
          <Input
            placeholder="e.g. Custom Label"
            value={fieldForm.fieldLabel}
            onChange={(e) =>
              setFieldForm((p) => ({ ...p, fieldLabel: e.target.value }))
            }
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Field type</label>
          <Select
            value={fieldForm.fieldType}
            onChange={(v) => setFieldForm((p) => ({ ...p, fieldType: v }))}
            options={[
              { value: "text", label: "Text" },
              { value: "date", label: "Date" },
              { value: "designation", label: "Designation (dropdown)" },
              { value: "department", label: "Department (dropdown)" },
              { value: "textarea", label: "Multi-line text" },
            ]}
            style={{ width: "100%", marginTop: 4 }}
          />
        </div>
        <div>
          <label>Display order</label>
          <InputNumber
            min={0}
            value={fieldForm.displayOrder}
            onChange={(v) => setFieldForm((p) => ({ ...p, displayOrder: v ?? 0 }))}
            style={{ width: "100%", marginTop: 4 }}
          />
        </div>
      </Modal>

      <Modal
        title="Send letter by email"
        open={emailModalVisible}
        onOk={handleEmailSubmit}
        onCancel={() => setEmailModalVisible(false)}
        confirmLoading={emailSending}
        okText="Send"
      >
        {emailConfigResolved?.EmailId && (
          <div style={{ marginBottom: 12 }}>
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>From: </span>
            <span>{emailConfigResolved.EmailId}</span>
          </div>
        )}
        <Input
          placeholder="To (recipient / employee email)"
          value={emailTo}
          onChange={(e) => setEmailTo(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Input
          placeholder="CC (optional, comma-separated)"
          value={emailCc}
          onChange={(e) => setEmailCc(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Checkbox
          checked={includeRecipientInCc}
          onChange={(e) => setIncludeRecipientInCc(e.target.checked)}
          style={{ marginBottom: 12 }}
        >
          Include recipient in CC
        </Checkbox>
        <Input
          placeholder="Subject"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <TextArea
          placeholder="Message (optional)"
          value={emailBody}
          onChange={(e) => setEmailBody(e.target.value)}
          rows={4}
        />
      </Modal>
    </div>
  );
}
